// MTO Cart Upsell — cart drawer quick-add carousel.
// Bound to `document` (not the carousel root) because the cart drawer's
// inner markup is re-rendered via the Section Rendering API on every cart
// update; a listener bound directly to a swapped-out node would stop
// working after the first add. This module script itself is only loaded
// once (outside the swapped region, next to cart-drawer.js), so a
// document-level delegated listener is the only handler that survives.
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

function updateArrowVisibility(root) {
  const viewport = root.querySelector('.mto-cu__viewport');
  const list = root.querySelector('[data-mto-cu-list]');
  if (!viewport || !list) return;
  const overflows = list.scrollWidth > list.clientWidth + 1;
  viewport.querySelectorAll('.mto-cu__arrow').forEach((arrow) => {
    arrow.hidden = !overflows;
  });
}

function updateAllUpsellArrows() {
  document.querySelectorAll('.mto-cu').forEach(updateArrowVisibility);
}

let updateArrowsRaf = 0;
function scheduleUpdateAllUpsellArrows() {
  if (updateArrowsRaf) return;
  updateArrowsRaf = requestAnimationFrame(() => {
    updateArrowsRaf = 0;
    updateAllUpsellArrows();
  });
}

updateAllUpsellArrows();
window.addEventListener('resize', scheduleUpdateAllUpsellArrows);
new MutationObserver((mutations) => {
  if (mutations.some((m) => m.addedNodes.length || m.removedNodes.length)) {
    scheduleUpdateAllUpsellArrows();
  }
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('click', async (e) => {
  const prevBtn = e.target.closest('[data-mto-cu-prev]');
  const nextBtn = e.target.closest('[data-mto-cu-next]');
  if (prevBtn || nextBtn) {
    const viewport = (prevBtn || nextBtn).closest('.mto-cu__viewport');
    const list = viewport && viewport.querySelector('[data-mto-cu-list]');
    if (list) {
      const amount = list.clientWidth * 0.8 * (nextBtn ? 1 : -1);
      list.scrollBy({ left: amount, behavior: 'smooth' });
    }
    return;
  }

  const btn = e.target.closest('[data-mto-cu-add]');
  if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;

  const variantId = btn.dataset.vid;
  if (!variantId) return;

  btn.classList.add('is-loading');

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const cfg = fetchConfig('javascript', { body: formData });
    const res = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    });
    const json = await res.json();

    if (json.status) {
      deferred.reject(new Error(json.message || 'Add to cart failed'));
      btn.classList.remove('is-loading');
      return;
    }

    const ajaxCart = await (await fetch(Theme.routes.cart_url + '.js')).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'mto-cart-upsell',
        sourceId: 'mto-cu',
        itemCount: 1,
        sections: json.sections,
        didError: false,
      },
    });

    btn.classList.remove('is-loading');
    btn.setAttribute('data-added', 'true');
    setTimeout(() => btn.removeAttribute('data-added'), 1200);
  } catch (err) {
    deferred.reject(err);
    btn.classList.remove('is-loading');
    console.error('[mto-cart-upsell]', err);
  }
});
